import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { centerCrop, makeAspectCrop, PercentCrop } from 'react-image-crop'
import CropImageDialog, { cropImage } from './CropImageDialog'

type UseImageInputArgs = {
  value: string | null | undefined
  setValue: (v: string | null | undefined) => void
  initialValue: string | null | undefined

  aspect?: number
  circularCrop?: boolean
}

export default function useImageInput({ value, setValue, initialValue, ...cropProps }: UseImageInputArgs) {
  const [adjustmentSource, setAdjustmentSource] = useState(initialValue)
  const [adjustment, setAdjustment] = useState<PercentCrop | undefined>()
  useEffect(() => {
    setAdjustmentSource(initialValue)
    setAdjustment(undefined)
  }, [initialValue])

  // Image selection
  const currentReaderRef = useRef<FileReader | null>(null)

  const { getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    onDrop: (files) => {
      currentReaderRef.current?.abort()

      const reader = new FileReader()
      reader.onabort = () => console.log('file reading was aborted')
      reader.onerror = () => console.log('file reading has failed')
      reader.onload = () => {
        const img = new Image()
        img.onload = () => {
          setAdjustmentSource(reader.result as any)
          if (cropProps.aspect) {
            const crop = centerCrop(
              makeAspectCrop({ unit: '%', width: 100 }, cropProps.aspect, img.naturalWidth, img.naturalHeight),
              img.naturalWidth,
              img.naturalHeight
            )
            const imageUrl = cropImage(img, crop)

            setValue(imageUrl)
            setAdjustment(crop)
          } else {
            setValue(reader.result as any)
            setAdjustment(undefined)
          }
        }
        img.src = reader.result as any
      }
      reader.readAsDataURL(files[0])
      currentReaderRef.current = reader
    },
    accept: {
      'image/*': ['.png', '.gif', '.jpeg', '.jpg'],
    },
  })

  // Cropping
  const [isAdjusting, setisAdjusting] = useState(false)

  const handleFinishAdjustment = useCallback(
    ({ crop, imageUrl }: { crop?: PercentCrop; imageUrl: string }) => {
      setisAdjusting(false)
      setAdjustment(crop)
      setValue(imageUrl)
    },
    [setisAdjusting, setAdjustment, setValue]
  )

  const handleAbortAdjustment = useCallback(() => setisAdjusting(false), [setisAdjusting])

  const children =
    adjustmentSource && isAdjusting ? (
      <CropImageDialog
        source={adjustmentSource}
        crop={adjustment}
        onClose={handleAbortAdjustment}
        onSubmit={handleFinishAdjustment}
        {...cropProps}
      />
    ) : null

  // Button props
  const selectButtonProps = useMemo(
    () => ({
      onClick: open,
    }),
    [open]
  )

  const discardButtonProps = useMemo(
    () => ({
      onClick: () => {
        setValue(initialValue)
        setAdjustmentSource(initialValue)
        setAdjustment(undefined)
      },
      disabled: value === initialValue,
    }),
    [setValue, value, initialValue]
  )

  const removeButtonProps = useMemo(
    () => ({
      onClick: () => {
        setValue(null)
        setAdjustmentSource(undefined)
        setAdjustment(undefined)
      },
      disabled: !value || !initialValue,
    }),
    [setValue, value, initialValue]
  )

  const adjustButtonProps = useMemo(
    () => ({
      onClick: () => setisAdjusting(true),
      disabled: !adjustmentSource,
    }),
    [setisAdjusting, adjustmentSource]
  )

  return {
    rootProps: getRootProps(),
    inputProps: getInputProps(),
    selectButtonProps,
    discardButtonProps,
    removeButtonProps,
    adjustButtonProps,
    children,
  }
}
